import type { StrategyItem } from '@/components/star/StrategyCard.vue'
import moment from 'moment'
import { getStarInheritedQuery } from '@/utils/useStarTheme'

const JFZT_PC_FRONTEND_TEST = 'https://test-h5.techgp.cn/jfzt-pc-frontend/index.html'
const JFZT_PC_FRONTEND_PROD = 'https://h5.chongnengjihua.com/jfzt-pc-frontend/index.html'

/** 星投量化专区：jfzt-pc-frontend 测试/生产环境判断 */
export function isStarJfztTestEnv() {
  const hostname = typeof location !== 'undefined' ? location.hostname : ''
  return hostname.includes('test') || hostname.includes('localhost')
}

export function getStarJfztPcFrontendBase() {
  return isStarJfztTestEnv() ? JFZT_PC_FRONTEND_TEST : JFZT_PC_FRONTEND_PROD
}

/** jfzt-pc-frontend 跳转时透传当前页公共路由参数（userName 兼容 quant 页） */
export function getStarJfztInheritedQuery(routeQuery?: Record<string, unknown>) {
  if (!routeQuery) return {}
  const inherited = getStarInheritedQuery(routeQuery)
  const result: Record<string, string> = {
    platform: 'htz',
    ...inherited,
  }
  if (result.username) {
    result.userName = result.username
    delete result.username
  }
  return result
}

function mergeQueryIntoUrl(url: string, query: Record<string, string>) {
  if (!Object.keys(query).length) return url

  const hashIndex = url.indexOf('#')
  if (hashIndex === -1) {
    const parsed = new URL(url)
    Object.entries(query).forEach(([key, value]) => {
      if (value) parsed.searchParams.set(key, value)
    })
    return parsed.toString()
  }

  const base = url.slice(0, hashIndex)
  const hash = url.slice(hashIndex + 1)
  const queryIndex = hash.indexOf('?')

  if (queryIndex === -1) {
    const params = new URLSearchParams(query)
    return `${base}#${hash}?${params.toString()}`
  }

  const hashPath = hash.slice(0, queryIndex)
  const params = new URLSearchParams(hash.slice(queryIndex + 1))
  Object.entries(query).forEach(([key, value]) => {
    if (value && !params.has(key)) params.set(key, value)
  })
  return `${base}#${hashPath}?${params.toString()}`
}

function applyStarRoomAccessParams(params: URLSearchParams, hasPermission: boolean) {
  if (hasPermission) {
    params.set('starInvest', 'true')
  } else {
    params.set('freeRoom', '100')
  }
}

/** 圈子文字直播 */
export function buildStarTextRoomUrl(
  roomNo: string,
  routeQuery?: Record<string, unknown>,
  hasPermission = true,
) {
  const params = new URLSearchParams({ roomNo })
  applyStarRoomAccessParams(params, hasPermission)
  const inherited = getStarJfztInheritedQuery(routeQuery)
  if (!inherited.userType) inherited.userType = '2'
  Object.entries(inherited).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return `${getStarJfztPcFrontendBase()}#/textRoom?${params.toString()}`
}

/** 视频直播 */
export function buildStarPcLiveUrl(
  roomNo: string,
  routeQuery?: Record<string, unknown>,
  hasPermission = true,
) {
  const params = new URLSearchParams({ r: roomNo, roomNo })
  applyStarRoomAccessParams(params, hasPermission)
  const inherited = getStarJfztInheritedQuery(routeQuery)
  if (!inherited.userType) inherited.userType = '2'
  Object.entries(inherited).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return `${getStarJfztPcFrontendBase()}#/pcLive?${params.toString()}`
}

/** 在 XP 客户端内嵌页打开链接 */
export function openStarJfztUrl(url: string, routeQuery?: Record<string, unknown>) {
  const finalUrl = mergeQueryIntoUrl(url, getStarJfztInheritedQuery(routeQuery))
  console.log('finalUrl', `gw://funccall?funcname=OnFireCommand;Command=cmdsingleweb;param=<params><web_inner_url>${finalUrl}</web_inner_url></params>`)
  window.open(
    `gw://funccall?funcname=OnFireCommand;Command=cmdsingleweb;param=<params><web_inner_url>${finalUrl}</web_inner_url></params>`,
  )
}

const SQUARE_TAG_SUFFIX = '解锁'
const LEGACY_TAG_SUFFIX = '用户专属'

/** 模版策略卡片右上角解锁标签 */
export function formatSquareUnlockTag(starLevel?: number, starTag?: string): string | undefined {
  if (starLevel === -2) return '初级用户解锁'

  const tag = (starTag || '').trim()
  if (tag) {
    if (tag === '免费用户') return '初级用户解锁'
    if (tag.endsWith(SQUARE_TAG_SUFFIX)) return tag
    if (tag.endsWith(LEGACY_TAG_SUFFIX)) return tag.replace(LEGACY_TAG_SUFFIX, SQUARE_TAG_SUFFIX)
    const base = tag.replace(/(解锁)$/, '')
    return `${base}${SQUARE_TAG_SUFFIX}`
  }

  if (starLevel != null && starLevel > 0) return `${starLevel}星${SQUARE_TAG_SUFFIX}`
  if (starLevel === -1) return '登录券商'

  return undefined
}

/** 模版策略列表项 → StrategyCard 数据 */
export function mapSquareItemToStrategy(item: any): StrategyItem {
  const chartData = (item.returnList || []).map((p: any) => ({
    date: String(p.d),
    strategy: (p.v ?? 0) * 100,
    benchmark: (p.b ?? 0) * 100,
  }))

  const leftTags: { content: string }[] = []
  if (item.typeName) leftTags.push({ content: item.typeName })
  else leftTags.push({ content: '组合量化' })

  return {
    id: item.taskID,
    name: item.name,
    type: 'group',
    quantType: 3,
    strategyReturn: item.cumRet != null ? item.cumRet * 100 : null,
    annRet: item.annRet != null ? item.annRet * 100 : null,
    maxDrawdown: item.maxDD != null ? item.maxDD * 100 : null,
    profitLossRatio: null,
    winRate: undefined,
    riskRatio: item.maxDD != null ? item.maxDD : 0,
    typeName: item.typeName || '',
    leftTags,
    starLevel: item.starLevel,
    starTag: item.starTag || '',
    hasPermission: item.hasPermission === true,
    rightTag: (() => {
      if (item.hasPermission === true) return undefined
      const content = formatSquareUnlockTag(item.starLevel, item.starTag)
      return content ? { content, url: '' } : undefined
    })(),
    chartData,
  }
}

export function formatStarTime(ts?: number | string) {
  if (!ts) return ''
  return moment(ts).format('YYYY-MM-DD HH:mm')
}

export function getNewsCover(item: any): string {
  const urls = item?.appImageUrlList || item?.attribute?.appImageUrlList
  if (Array.isArray(urls) && urls.length) return urls[0]
  if (typeof urls === 'string' && urls) {
    try {
      const parsed = JSON.parse(urls)
      if (Array.isArray(parsed) && parsed.length) return parsed[0]
    } catch (_) {
      return urls
    }
  }
  return item?.attribute?.listImageUrl || item?.attribute?.imageUrl || ''
}

export function formatHitCount(count?: number) {
  if (count == null) return ''
  if (count >= 10000) return `${(count / 10000).toFixed(1)}万浏览`
  return `${count}浏览`
}

/** 圈子卡片 - 提取观点消息列表（不含公告） */
export function isNoticeMessageType(messageType?: string) {
  return !!messageType && messageType.indexOf('type_notice') >= 0
}

export function isTextMessageType(messageType?: string) {
  return !!messageType && messageType.indexOf('text') >= 0 && messageType !== 'text_json'
}

export function isCaseStockMessage(messageType?: string) {
  return !!messageType && messageType.indexOf('case_stock') >= 0
}

export function isImageMessageType(messageType?: string) {
  return !!messageType && messageType.indexOf('img') >= 0
}

export function isVoiceMessageType(messageType?: string) {
  return !!messageType && messageType.indexOf('voice') >= 0
}

export function isLinkUrlMessageType(messageType?: string) {
  return !!messageType && messageType.indexOf('link_url') >= 0
}

export function parseJsonMessage<T = any>(message?: string): T | null {
  if (!message) return null
  try {
    return JSON.parse(message.replace(/\n/g, '\\n').replace(/\r/g, '\\r'))
  } catch {
    try {
      return JSON.parse(message)
    } catch {
      return null
    }
  }
}

export function getCaseStockInfo(message?: string) {
  if (!message) return { stockName: '', labelName: '' }

  const parsed = parseJsonMessage<any>(message)
  if (parsed && typeof parsed === 'object') {
    return {
      stockName: parsed.stockName || '',
      labelName: parsed.labelName || '',
    }
  }

  if (typeof message === 'string' && message.includes('¥*') && message.includes('*¥')) {
    const match = message.match(/¥\*([^$]+)\(([^)]+)\)\$?\*¥/)
    if (match) {
      const fullContent = match[1]
      const tagPatterns = ['新增案例', '案例跟踪', '取消关注', '教学加仓', '教学减仓', '自定义标签']
      let tag = ''
      let stockName = fullContent
      for (const pattern of tagPatterns.sort((a, b) => b.length - a.length)) {
        if (fullContent.startsWith(pattern)) {
          tag = pattern
          stockName = fullContent.slice(pattern.length)
          break
        }
      }
      return { stockName, labelName: tag }
    }
  }

  return { stockName: '', labelName: '' }
}

export function getCircleMessagePreview(point?: Record<string, any>, withLink = false) {
  const message = point?.message
  const messageType = point?.messageType || ''

  if (!message) return withLink ? { text: '', url: '' } : ''

  if (isLinkUrlMessageType(messageType)) {
    const data = parseJsonMessage<any>(message)
    const text = data?.text || data?.links?.[0]?.linkUrl || message
    const url = data?.links?.[0]?.linkUrl || ''
    return withLink ? { text, url } : text
  }

  if (messageType.indexOf('link_room') >= 0) {
    const data = parseJsonMessage<any>(message)
    const roomName = data?.title || data?.periodName || ''
    return roomName ? `[直播]${roomName}` : '[直播]'
  }

  if (messageType.indexOf('text_json') >= 0) {
    const data = parseJsonMessage<any>(message)
    const title = data?.title || data?.subTitle || ''
    return title ? `[图文观点]${title}` : '[图文观点]'
  }

  if (
    messageType.indexOf('link_course') >= 0 ||
    messageType.indexOf('link_subject') >= 0 ||
    messageType.indexOf('link_sub_course') >= 0
  ) {
    const data = parseJsonMessage<any>(message)
    const name = data?.courseName || data?.groupName || data?.contentSubName || data?.chapterName || ''
    return name ? `[课程]${name}` : '[课程]'
  }

  if (messageType.indexOf('link_news') >= 0) {
    const data = parseJsonMessage<any>(message)
    const title = data?.title || ''
    return title ? `[资讯]${title}` : '[资讯]'
  }

  if (messageType.indexOf('link_activity') >= 0) {
    const data = parseJsonMessage<any>(message)
    const title = data?.title || data?.subtitle || ''
    return title ? `[活动]${title}` : '[活动]'
  }

  if (isImageMessageType(messageType)) return '[图片]'
  if (isVoiceMessageType(messageType)) return '[语音]'

  return formatCircleMessage(message)
}

export function getCirclePoints(item: any, limit?: number) {
  const points = item?.points
  if (!Array.isArray(points)) return []
  const filtered = points.filter(
    (p) => p?.message?.trim() && !isNoticeMessageType(p.messageType),
  )
  return limit ? filtered.slice(0, limit) : filtered
}

/** 圈子卡片 - 介绍语 */
export function getCircleIntro(item: any) {
  return (
    item?.courseSimpleDTO?.introduction ||
    item?.courseSimpleDTO?.shortIntroduction ||
    item?.roomDTO?.introduction ||
    ''
  )
}

/** 圈子卡片 - 格式化消息文本（去 HTML） */
export function formatCircleMessage(message?: string) {
  if (!message) return ''
  return message
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}
